import { useState, useRef, RefObject } from "react";
import {
  Button,
  Modal,
  DateInput,
  Grid,
  TextAreaInput,
} from "@monolith-forensics/monolith-ui";
import type { DropDownItem } from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";

import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";
import { SignatureField, SignatureFieldRef } from "@/Features";

import { useQuery } from "@tanstack/react-query";

import type { BaseCocModalProps, CocSignatureObject } from "../Coc.types";
import type { Location } from "@/pages/Locations/LocationsSection";
import EvidenceCocAPI from "@/api/evidence/coc";
import LocationCocSelector from "@/components/Forms/components/Selectors/LocationCocSelector";
import { createSignature } from "../utils";

interface MoveCocModalProps extends BaseCocModalProps {}

interface MoveFormData {
  new_location: Location | null;
  reason: string;
  timestamp: string;
}

interface MoveReasonOption {
  reason: string;
  group?: string;
}

const moveValidationSchema = z.object({
  new_location: z
    .object(
      {
        location_id: z.number(),
        location_name: z.string(),
      },
      {
        required_error: "New location is required",
      }
    )
    .refine((val) => val !== null && val !== undefined, {
      message: "New location is required",
    }),
  reason: z
    .string({
      required_error: "Move reason is required",
    })
    .min(1, { message: "Move reason is required" }),
  timestamp: z
    .string({
      required_error: "Timestamp is required",
    })
    .min(1, { message: "Timestamp is required" }),
});

const MoveCocModal: React.FC<MoveCocModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<MoveFormData>({
    mode: "uncontrolled",
    initialValues: {
      new_location: null,
      reason: "",
      timestamp: new Date().toISOString(),
    },
  });

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const movedFromSig = useRef<SignatureFieldRef>();
  const movedToSig = useRef<SignatureFieldRef>();

  const moveReasonsQuery = useQuery({
    queryKey: ["evidence", "coc-move-reasons"],
    queryFn: () => EvidenceCocAPI.getCocMoveReasons(),
    enabled: show,
  });

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(moveValidationSchema)(
      formData
    );
    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      form.setErrors(validationResult?.errors);
      return;
    }

    setSubmitting(true);
    try {
      const ipAddress = await getIpAddress();

      const signature: CocSignatureObject = {
        custody_from_sig: createSignature(
          movedFromSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
        custody_to_sig: createSignature(
          movedToSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
      };

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
        },
        evidenceInfo: {
          evidence_id: defaultInfo.evidence_id,
          uuid: defaultInfo.uuid,
          evidence_number: defaultInfo.evidence_number,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          new_location_id: formData.new_location?.location_id || 0,
          new_location_name: formData.new_location?.location_name || "",
          current_location_name: defaultInfo.location_name,
          reason: formData.reason || "Evidence item moved.",
          timestamp: db_timestamp(formData.timestamp, true),
          signature: JSON.stringify(signature),
          coc_type: "Move",
        },
      };

      await EvidenceCocAPI.moveEvidence(requestObj);

      enqueueSnackbar(
        `Evidence Item Moved to ${
          formData.new_location?.location_name || "selected location"
        }`,
        {
          variant: "success",
        }
      );

      onSubmit?.(requestObj);
    } catch (error) {
      console.error("Error moving evidence:", error);
      enqueueSnackbar(`Error moving evidence item.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose?.();
  };

  const reasonOptions: DropDownItem[] = (
    (moveReasonsQuery.data || []) as MoveReasonOption[]
  ).map((item) => ({
    label: item.reason,
    value: item.reason,
    group: item.group,
  }));

  return (
    <Modal open={show} onClose={onClose} style={{ width: 600 }}>
      <Modal.Title>Move Evidence Item</Modal.Title>
      <div style={{ margin: "20px 0px" }}>
        <Grid col={1}>
          <LocationCocSelector
            label="New Location"
            required
            placeholder="Select location"
            key={form.key("new_location")}
            {...form.getInputProps("new_location")}
          />
          <TextAreaInput
            label="Move Reason"
            required
            placeholder="Select or add new..."
            actionMenuOptions={{
              variant: "ellipsis",
              menuOptions: {
                data: reasonOptions,
                grouped: true,
                onItemSelect: (item: DropDownItem) => {
                  const current = form.getValues().reason;
                  form.setFieldValue(
                    "reason",
                    current
                      ? `${current}${String(item.value)}`
                      : String(item.value)
                  );
                },
              },
            }}
            minRows={2}
            maxRows={4}
            key={form.key("reason")}
            {...form.getInputProps("reason")}
          />
          <DateInput
            label="Timestamp"
            required
            format="YYYY-MM-DD HH:mm:ss"
            enableCalendar
            arrow
            key={form.key("timestamp")}
            {...form.getInputProps("timestamp")}
          />
          <div>
            <SignatureField
              sigFieldRef={movedFromSig}
              label="Moved By Signature"
              useESignature={true}
            />
          </div>
          <div>
            <SignatureField
              sigFieldRef={movedToSig}
              label="Moved To Signature"
            />
          </div>
        </Grid>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Move Evidence
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default MoveCocModal;
